import React, { useRef } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import {
  PRICE_COUNTER_PROPOSAL_THRESHOLD,
  PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD,
} from 'Store/booking/proposals/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { AlternativeDateSuggestionHeader } from 'Components/widgets/alternative-proposals/alternative-date-suggestion/AlternativeDateSuggestionHeader';
import { AlternativeDurationSuggestions } from 'Components/widgets/alternative-proposals/alternative-duration-suggestions/AlternativeDurationSuggestions';
import { AlternativePrice } from 'Components/widgets/alternative-proposals/alternative-price/AlternativePrice';
import { AlternativeProposalHeader } from 'Components/widgets/alternative-proposals/alternative-proposal-header/AlternativeProposalHeader';
import { AlternativeDateSuggestion } from './alternative-date-suggestion/AlternativeDateSuggestion';
import { AlternativeProposalsSeparatorDashed } from './AlternativeProposalsSeparatorDashed';
import { AlternativeResort } from './alternative-resort/AlternativeResort';
export type AlternativeProposalsProps = Readonly<{
  bookingEngineEntryPoint?: string;
  disableAlternativePrice?: boolean;
  goToBookingEngine?: () => Promise<any>;
  i18n: I18N;
  onClose?: () => void;
  onSelectNewDuration: (arg0: { proposalIds: ReadonlyArray<string>; duration: number }) => void;
  onSubmit: (args: any) => void;
  proposals: ReadonlyArray<Proposal>;
  resort: Product;
  scrollToBookingEngineEntryPoint?: () => void;
  searchCriteria: ProposalSearchCriteria;
  selectResortHandler?: (id: string, name: string) => unknown;
  suggestions: BookingSuggestions;
}>;
export const AlternativeProposals = ({
  bookingEngineEntryPoint,
  disableAlternativePrice = false,
  goToBookingEngine,
  i18n,
  onClose,
  onSelectNewDuration,
  onSubmit,
  proposals,
  resort,
  scrollToBookingEngineEntryPoint,
  searchCriteria: { adultsCount, arrivalDate, departureCity, duration, kidsCount },
  searchCriteria,
  selectResortHandler,
  suggestions: { dates, resorts, durations },
}: AlternativeProposalsProps) => {
  const appContext = useAppContext();
  const track = useTracking({
    actionGA: 'checkPricesKO',
    categoryGA: 'search',
  });
  const separatorComponent = useRef(null);
  const proposal = proposals[0];
  const shouldDisplayAlternativePriceCriteria =
    !disableAlternativePrice &&
    proposal?.price?.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD &&
    (appContext.isSeller || proposal.price.gap < PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD);
  const shouldDisplayAlternativePriceTitle =
    !disableAlternativePrice && proposal?.price?.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD;

  const seeOtherProposalsCallback = () => {
    if (separatorComponent.current) {
      separatorComponent.current.scrollIntoView();
    }
  };

  const atLeastOneDateCounterProposal = dates.length > 0;
  const atLeastOneResortCounterProposal = resorts.length > 0;
  const atLeastOneDurationCounterProposal = durations.length > 0;
  const hasSuggestions =
    atLeastOneDateCounterProposal ||
    atLeastOneResortCounterProposal ||
    atLeastOneDurationCounterProposal;

  const trackingData = (labelGA: string) => {
    track({
      labelGA,
    });
  };

  return (
    <div data-testid="AlternativeProposalsModal">
      {shouldDisplayAlternativePriceTitle && (
        <SiteContainer className="my-40">
          <AlternativeProposalHeader
            proposal={proposal}
            title={t('booking.counterProposalPopIn.rateCounterProposalTitle')}
          />
        </SiteContainer>
      )}

      {shouldDisplayAlternativePriceCriteria && (
        <SiteContainer className="my-40">
          <AlternativePrice
            bookingEngineEntryPoint={bookingEngineEntryPoint}
            continueToBookingEngine={goToBookingEngine}
            departureCities={i18n.bookingCriteria?.departureCities || []}
            hasOtherProposals={hasSuggestions}
            onModify={
              onClose
                ? () => {
                    onClose();

                    if (scrollToBookingEngineEntryPoint) {
                      scrollToBookingEngineEntryPoint();
                    }
                  }
                : undefined
            }
            proposals={proposals}
            resort={resort}
            searchCriteria={searchCriteria}
            seeOtherProposalsCallback={seeOtherProposalsCallback}
          />
        </SiteContainer>
      )}

      {hasSuggestions && shouldDisplayAlternativePriceCriteria && (
        <AlternativeProposalsSeparatorDashed ref={separatorComponent} />
      )}

      {hasSuggestions && !shouldDisplayAlternativePriceTitle && !disableAlternativePrice ? (
        <SiteContainer className="my-40">
          <AlternativeDateSuggestionHeader
            arrivalDate={arrivalDate}
            duration={duration}
            resort={resort}
          />
        </SiteContainer>
      ) : (
        <SiteContainer>
          <AlternativeProposalHeader
            className="my-40"
            title={t('booking.counterProposalPopIn.otherProposals')}
          />
        </SiteContainer>
      )}

      {atLeastOneDateCounterProposal && (
        <SiteContainer className="my-40">
          <AlternativeDateSuggestion
            dates={dates}
            onClose={onClose}
            onSubmit={onSubmit}
            resort={resort}
            trackingData={trackingData}
          />
        </SiteContainer>
      )}

      {atLeastOneDateCounterProposal &&
        (atLeastOneDurationCounterProposal || atLeastOneResortCounterProposal) && (
          <AlternativeProposalsSeparatorDashed />
        )}

      {atLeastOneDurationCounterProposal && (
        <SiteContainer className="my-40">
          <AlternativeDurationSuggestions
            durations={durations}
            onSelectNewDuration={onSelectNewDuration}
            trackingData={trackingData}
          />
        </SiteContainer>
      )}

      {atLeastOneDurationCounterProposal && atLeastOneResortCounterProposal && (
        <AlternativeProposalsSeparatorDashed />
      )}

      {atLeastOneResortCounterProposal && (
        <SiteContainer className="my-40">
          <AlternativeResort
            adultsNumber={adultsCount}
            arrivalDate={arrivalDate}
            departureCity={departureCity}
            duration={duration}
            kidsNumber={kidsCount}
            scrollToBookingEngineEntryPoint={scrollToBookingEngineEntryPoint}
            selectResortHandler={selectResortHandler}
            suggestions={resorts}
          />
        </SiteContainer>
      )}
      {!appContext.isCmta && (
        <SiteContainer className="mt-40 border-t-1 border-grey">
          <div className="flex items-center justify-center gap-5 py-20 text-18 font-bold text-black">
            <Icon className="shrink-0 text-green" hasRtl name={ICONS.PHONE_BUBBLE} width="3em" />
            <p>
              {t('booking.counterProposalPopIn.contactCallCenter')}{' '}
              <span className="text-green">
                {t('booking.counterProposalPopIn.callCenterNumber')}
              </span>{' '}
              <span className="text-12 font-normal">
                {t('booking.counterProposalPopIn.callCost')}
              </span>
            </p>
          </div>
        </SiteContainer>
      )}
    </div>
  );
};
